import { inject, Injectable, Injector } from '@angular/core';

import { AuthService } from '@ph-core/services/auth/auth.service';
import { markPostLogoutAll, recordDavinciSsoDefer } from '@ph-core/services/auth/auth-sso-defer';
import { logSloInfo, logSloWarn } from '@ph-core/services/auth/slo-log';
import {
  PHOENIX_AUTH_BROADCAST_CHANNEL,
  PHOENIX_CROSS_TAB_LOGOUT_TYPE,
} from '@ph-core/services/auth/slo-logout.constants';
import { ConfigService } from '@ph-core/services/config/config.service';
import { ErrorService } from '@ph-core/services/error/error.service';
import { LoggerService } from '@ph-core/services/logger/logger.service';
import { OktaService } from '@ph-core/services/okta/okta.service';
import { RouterService } from '@ph-core/services/router/router.service';
import { UserStore, UserStoreType } from '@ph-store/user/user.store';

export type LogoutMode = 'portal-only' | 'full-slo';

export interface ExecuteLogoutOptions {
  mode: LogoutMode;
  /** Conflict page: keep SSO error visible until navigate away */
  preserveErrorMessage?: boolean;
  /** Sets post-logout-all flags and stops SSO probes before clearing session */
  prepareForLogoutAll?: boolean;
}

export interface IdpInitiatedLogoutParams {
  iss?: string;
  sid?: string;
}

/**
 * Okta OIDC front-channel SLO helpers.
 *
 * SP-initiated: executeLogout({ mode: 'full-slo' }) → oktaAuth.signOut with id_token_hint.
 * IdP-initiated: Okta loads /slo-logout?iss=&sid= in a hidden iframe → handleIdpInitiatedLogout.
 */
@Injectable({ providedIn: 'root' })
export class SloLogoutService {
  private configService = inject(ConfigService);
  private logger = inject(LoggerService);
  private oktaService = inject(OktaService);
  private routerService = inject(RouterService);
  private errorService = inject(ErrorService);
  private injector = inject(Injector);

  private broadcastChannel: BroadcastChannel | undefined;
  private idpVisibilityListenerBound = false;
  private idpProbeInFlight = false;

  private get userStore(): UserStoreType {
    return this.injector.get(UserStore);
  }

  /** URL to register as Okta participateSlo.logoutRequestUrl for this portal origin. */
  getLogoutRequestUrl(origin: string = window.location.origin): string {
    const env = this.configService.environment;
    if (env.okta.applicationInRedirectUrls) {
      return `${origin}/${env.application}/slo-logout`;
    }
    return `${origin}/slo-logout`;
  }

  initCrossTabLogoutListener(): void {
    this.initIdpSessionVisibilityListener();

    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    try {
      this.broadcastChannel = new BroadcastChannel(PHOENIX_AUTH_BROADCAST_CHANNEL);
      this.broadcastChannel.onmessage = (event: MessageEvent) => {
        if (event.data?.type !== PHOENIX_CROSS_TAB_LOGOUT_TYPE) {
          return;
        }
        void this.handleCrossTabBroadcastLogout();
      };
    } catch {
      /* ignore */
    }
  }

  broadcastLogout(): void {
    try {
      const channel = this.broadcastChannel ?? new BroadcastChannel(PHOENIX_AUTH_BROADCAST_CHANNEL);
      channel.postMessage({ type: PHOENIX_CROSS_TAB_LOGOUT_TYPE });
      if (!this.broadcastChannel) {
        channel.close();
      }
      logSloInfo(this.logger, 'BroadcastChannel cross-tab logout sent on this origin.', {
        application: this.configService.environment.application,
      });
    } catch (error) {
      logSloWarn(this.logger, 'BroadcastChannel unavailable.', { error });
    }
  }

  async executeLogout(options: ExecuteLogoutOptions): Promise<void> {
    const environment = this.configService.environment;

    if (options.prepareForLogoutAll) {
      this.injector.get(AuthService).prepareForLogoutAll({
        preserveOktaTokens: options.mode === 'full-slo',
        hideError: !options.preserveErrorMessage,
      });
    }

    logSloInfo(this.logger, 'executeLogout started', {
      mode: options.mode,
      application: environment.application,
      stage: environment.type,
    });

    const useFullSlo = options.mode === 'full-slo' && environment.auth.sso;

    // Same-origin sibling tabs get this via broadcast; initiator needs it too so post-signOut
    // login skips silent SSO (prepareForLogoutAll already sets it for "End all sessions").
    if (useFullSlo && !options.prepareForLogoutAll) {
      markPostLogoutAll(environment.application);
    }

    this.userStore.clearLocalPortalSession({
      preservePreviousUser: true,
      preserveOktaTokens: useFullSlo,
    });

    this.broadcastLogout();

    if (useFullSlo) {
      try {
        await this.signOutFromOkta();
      } catch (error) {
        logSloWarn(this.logger, 'Okta signOut failed — navigating to login.', { error });
        this.errorService.hideErrorMessage();
        void this.routerService.navigateToLogin();
      }
      return;
    }

    this.errorService.hideErrorMessage();
    void this.routerService.navigateToLogin();
  }

  async signOutFromOkta(): Promise<void | boolean> {
    // ponytail: Okta Sign-out redirect URIs are bare origin (no /login); oktaSignOut default matches that
    logSloInfo(this.logger, 'signOutFromOkta');
    return this.oktaService.oktaSignOut();
  }

  /**
   * Handles Okta IdP-initiated front-channel logout (GET with iss/sid query params).
   * Returns false when the issuer does not match this deployment.
   */
  handleIdpInitiatedLogout(params: IdpInitiatedLogoutParams): boolean {
    const expectedIssuer = this.configService.environment.okta.config.SSOCredentials.issuer;
    const { iss, sid } = params;

    if (iss && iss !== expectedIssuer) {
      logSloWarn(this.logger, 'IdP-initiated logout ignored — issuer mismatch', { iss, expectedIssuer });
      return false;
    }

    logSloInfo(this.logger, 'IdP-initiated front-channel logout accepted', { iss, sid });

    this.userStore.clearLocalPortalSession({ preservePreviousUser: true });
    this.broadcastLogout();
    return true;
  }

  /** Same-origin sibling tab received logout broadcast — clear portal session and go to login. */
  async handleCrossTabBroadcastLogout(): Promise<void> {
    logSloInfo(this.logger, 'Cross-tab logout received', {
      application: this.configService.environment.application,
    });

    this.injector.get(AuthService).cancelAllSubscriptions();
    markPostLogoutAll(this.configService.environment.application);
    this.userStore.clearLocalPortalSession({ preservePreviousUser: true });
    this.errorService.hideErrorMessage();
    void this.routerService.navigateToLogin();
  }

  /** Foreign Okta session alive (cross-portal probe) — warning screen until user End all sessions. */
  async handleIdpSessionEject(): Promise<void> {
    logSloInfo(this.logger, 'Foreign Okta IdP session — routing to conflict page.', {
      application: this.configService.environment.application,
    });

    this.injector.get(AuthService).cancelAllSubscriptions();
    markPostLogoutAll(this.configService.environment.application);
    this.userStore.clearLocalPortalSession({ preservePreviousUser: true });
    // After clearLocalPortalSession — that clears the defer via clearSsoTabState. Persisting it here
    // keeps the conflict page across a refresh; "End all sessions" clears it again.
    recordDavinciSsoDefer(this.configService.environment.application, null);
    this.routerService.navigateToSsoConflict();
  }

  /** Okta session gone (cross-portal probe) — login widget can render without a foreign cookie. */
  async handleIdpProbeEjectToLogin(): Promise<void> {
    logSloInfo(this.logger, 'Okta IdP session gone — routing to login.', {
      application: this.configService.environment.application,
    });

    this.injector.get(AuthService).cancelAllSubscriptions();
    markPostLogoutAll(this.configService.environment.application);
    this.userStore.clearLocalPortalSession({ preservePreviousUser: true });
    this.errorService.hideErrorMessage();
    void this.routerService.navigateToLogin();
  }

  /** Conflict-page "End all sessions": portal + Okta IdP so sibling tabs can see the session is gone. */
  async logoutAllFromConflict(): Promise<void> {
    await this.executeLogout({
      mode: 'full-slo',
      preserveErrorMessage: true,
      prepareForLogoutAll: true,
    });
  }

  /** When this window is shown or focused, ask Okta if the IdP session still exists. */
  initIdpSessionVisibilityListener(): void {
    if (this.idpVisibilityListenerBound || typeof document === 'undefined') {
      return;
    }

    this.idpVisibilityListenerBound = true;
    const onWindowReturned = (): void => {
      void this.probeIdpSessionOnVisible();
    };
    document.addEventListener('visibilitychange', onWindowReturned);
    window.addEventListener('focus', onWindowReturned);
  }

  /**
   * Returns true when this tab was ejected (login, conflict, or Access Denied).
   * Called on visibility/focus and once at AuthService boot when the portal session is alive.
   */
  async probeIdpSessionOnVisible(): Promise<boolean> {
    if (document.hidden || this.idpProbeInFlight) {
      return false;
    }

    const environment = this.configService.environment;
    const loggedIn = this.userStore.isLoggedIn();
    const ssoScenario = this.errorService.error().ssoScenario;
    const onSsoErrorPage = !!ssoScenario;
    if (!environment.auth.sso || (!loggedIn && !onSsoErrorPage)) {
      return false;
    }

    this.idpProbeInFlight = true;
    try {
      const sessionStillOurs = loggedIn
        ? await this.oktaService.isBoundIdpSessionCurrent()
        : await this.oktaService.hasIdpSession();
      if (sessionStillOurs) {
        return false;
      }

      logSloInfo(this.logger, 'Okta IdP session no longer ours — ejecting tab.', {
        application: environment.application,
        loggedIn,
        ssoScenario,
      });

      if (await this.oktaService.hasIdpSession()) {
        await this.handleIdpSessionEject();
      } else {
        await this.handleIdpProbeEjectToLogin();
      }
      return true;
    } finally {
      this.idpProbeInFlight = false;
    }
  }
}
